import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';
import { subscription as subscriptionApi } from '../api';
import Modal from '../components/Modal';

export default function Pricing() {
  const navigate = useNavigate();
  const { plan, planStatus, planExpiresAt, invoicesThisMonth, invoiceLimit, refresh } = useSubscription();
  const [billing, setBilling] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [cancellingPlan, setCancellingPlan] = useState(false);

  const price = billing === 'monthly' ? 499 : 4499;
  const perMonth = billing === 'annual' ? Math.round(4499 / 12) : 499;
  const saving = billing === 'annual' ? (499 * 12) - 4499 : 0;

  const handleUpgrade = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await subscriptionApi.create(billing);
      const { subscription_id, razorpay_key } = res.data;

      const options = {
        key: razorpay_key,
        subscription_id,
        name: 'BillTrack by SanovaTech',
        description: `Pro Plan - ${billing === 'monthly' ? '₹499/month' : '₹4,499/year'}`,
        image: 'https://billtrack.sanovatech.in/logo192.png',
        handler: async (response) => {
          try {
            await subscriptionApi.verify({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature,
              billing_cycle: billing,
            });
            await refresh();
            navigate('/settings?tab=billing');
          } catch (err) {
            setError('Payment verified but activation failed. Contact support.');
          }
        },
        prefill: {},
        theme: { color: '#1e3a5f' },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        setError(`Payment failed: ${response.error.description}`);
      });
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initiate payment');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancellingPlan(true);
    try {
      await subscriptionApi.cancel();
      await refresh();
      setShowCancelConfirm(false);
      setCancelSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel subscription');
      setShowCancelConfirm(false);
    } finally {
      setCancellingPlan(false);
    }
  };

  const isPro = plan === 'pro';

  return (
    <div className="max-w-4xl mx-auto py-10 px-4">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold text-gray-900">Simple, Transparent Pricing</h1>
        <p className="text-gray-500 mt-2">Start free. Upgrade when you need more.</p>
      </div>

      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-4 mb-8">
        <button
          onClick={() => setBilling('monthly')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${billing === 'monthly' ? 'bg-primary-700 text-white' : 'bg-gray-100 text-gray-600'}`}
        >
          Monthly
        </button>
        <button
          onClick={() => setBilling('annual')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${billing === 'annual' ? 'bg-primary-700 text-white' : 'bg-gray-100 text-gray-600'}`}
        >
          Annual
          <span className="ml-2 bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">Save ₹{(499*12-4499).toLocaleString()}</span>
        </button>
      </div>

      {/* Plan cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Free Plan */}
        <div className={`card border-2 ${!isPro ? 'border-primary-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Free</h2>
            {!isPro && <span className="bg-primary-100 text-primary-700 text-xs font-medium px-2 py-1 rounded-full">Current Plan</span>}
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-1">₹0</p>
          <p className="text-sm text-gray-500 mb-6">Forever free</p>
          <ul className="space-y-3 text-sm text-gray-600 mb-6">
            <li className="flex items-center gap-2"><span className="text-green-500">✓</span> 5 invoices per month</li>
            <li className="flex items-center gap-2"><span className="text-green-500">✓</span> PDF download</li>
            <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Payment recording</li>
            <li className="flex items-center gap-2"><span className="text-green-500">✓</span> WhatsApp sharing</li>
            <li className="flex items-center gap-2"><span className="text-red-400">✗</span> Email sending</li>
            <li className="flex items-center gap-2"><span className="text-red-400">✗</span> Auto reminders</li>
          </ul>
          {!isPro && (
            <div className="text-xs text-gray-500 text-center">
              {invoicesThisMonth}/{invoiceLimit} invoices used this month
            </div>
          )}
        </div>

        {/* Pro Plan */}
        <div className={`card border-2 ${isPro ? 'border-primary-700' : 'border-gray-200'} relative`}>
          {!isPro && (
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-700 text-white text-xs font-medium px-3 py-1 rounded-full">
              Recommended
            </div>
          )}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Pro</h2>
            {isPro && <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-1 rounded-full">
              {planStatus === 'cancelled' ? 'Cancelled' : 'Active'}
            </span>}
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-1">
            ₹{perMonth.toLocaleString()}<span className="text-lg font-normal text-gray-500">/mo</span>
          </p>
          {billing === 'annual' && (
            <p className="text-sm text-gray-500 mb-1">₹{price.toLocaleString()} billed annually</p>
          )}
          <p className="text-sm text-gray-500 mb-6">
            {billing === 'annual' ? `Save ₹${saving.toLocaleString()} vs monthly` : 'Billed monthly'}
          </p>
          <ul className="space-y-3 text-sm text-gray-600 mb-6">
            <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Unlimited invoices</li>
            <li className="flex items-center gap-2"><span className="text-green-500">✓</span> PDF download</li>
            <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Email sending</li>
            <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Auto payment reminders</li>
            <li className="flex items-center gap-2"><span className="text-green-500">✓</span> Payment recording</li>
            <li className="flex items-center gap-2"><span className="text-green-500">✓</span> WhatsApp sharing</li>
            <li className="flex items-center gap-2"><span className="text-green-500">✓</span> All future features</li>
          </ul>
          {isPro ? (
            <div className="space-y-3">
              <div className="text-xs text-gray-500 text-center">
                {planStatus === 'cancelled'
                  ? `Access until ${new Date(planExpiresAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`
                  : `Renews ${new Date(planExpiresAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`
                }
              </div>
              {planStatus !== 'cancelled' && !cancelSuccess && (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="w-full text-sm text-red-600 hover:text-red-700 border border-red-300 hover:border-red-500 hover:bg-red-50 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel Subscription
                </button>
              )}
              {planStatus === 'cancelled' && (
                <button
                  onClick={handleUpgrade}
                  disabled={loading}
                  className="btn-primary w-full disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Reactivate Pro'}
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={handleUpgrade}
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50"
            >
              {loading ? 'Processing...' : `Upgrade to Pro — ₹${price.toLocaleString()}`}
            </button>
          )}
        </div>
      </div>

      {cancelSuccess && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm text-center">
          ✅ Subscription cancelled. You'll retain Pro access until{' '}
          <strong>
            {planExpiresAt
              ? new Date(planExpiresAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
              : 'end of billing period'}
          </strong>.
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm text-center">
          {error}
        </div>
      )}

      <p className="text-center text-xs text-gray-400 mt-8">
        Secured by Razorpay · BillTrack by SanovaTech
      </p>

      <Modal
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        title="Cancel Subscription"
        size="sm"
      >
        <div className="text-center py-2">
          <div className="text-4xl mb-4">⚠️</div>
          <p className="text-gray-700 mb-2">
            After cancellation, you'll retain Pro access until{' '}
            <strong>
              {planExpiresAt
                ? new Date(planExpiresAt).toLocaleDateString('en-IN', {
                    day: '2-digit', month: 'short', year: 'numeric'
                  })
                : 'end of billing period'}
            </strong>.
          </p>
          <p className="text-gray-500 text-sm mb-6">
            Do you want to continue?
          </p>
          <button
            onClick={handleCancelSubscription}
            disabled={cancellingPlan}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg mb-3 disabled:opacity-50"
          >
            {cancellingPlan ? 'Cancelling...' : 'Yes, Cancel Subscription'}
          </button>
          <button
            onClick={() => setShowCancelConfirm(false)}
            className="btn-secondary w-full"
          >
            No, Keep Pro
          </button>
        </div>
      </Modal>
    </div>
  );
}
